/**
 * キャリアAIチャット用 SSE クライアント (fetch + ReadableStream)。
 *
 * EventSource は POST 不可なので fetch + ReadableStream で POST ストリーミングを実現。
 * バックエンドは text/event-stream を返却（events: meta / thinking / text / complete / done / error）。
 */

const ENDPOINT_PREFIX = process.env.VUE_APP_ENDPOINT_PREFIX || '/'
const BASE = `${ENDPOINT_PREFIX}api/career-ai`

/**
 * SSE イベントを 1 つずつ yield するジェネレータ用ユーティリティ。
 * 行頭が `event: ` または `data: ` のフレームを組み立て、空行で確定する。
 */
function parseSSEChunks(text, buffer) {
  const combined = buffer + text
  const frames = combined.split('\n\n')
  const remaining = frames.pop() // 最後は未完成の可能性
  const events = []
  for (const frame of frames) {
    if (!frame.trim()) continue
    let eventName = 'message'
    const dataLines = []
    for (const line of frame.split('\n')) {
      if (line.startsWith('event:')) eventName = line.substring(6).trim()
      else if (line.startsWith('data:'))
        dataLines.push(line.substring(5).trim())
    }
    if (dataLines.length > 0) {
      try {
        events.push({
          event: eventName,
          data: JSON.parse(dataLines.join('\n'))
        })
      } catch (_) {
        events.push({ event: eventName, data: dataLines.join('\n') })
      }
    }
  }
  return { events, buffer: remaining }
}

function getCsrfToken() {
  // Django csrftoken を cookie から取り出す
  const m = document.cookie.match(/(?:^|;\s*)csrftoken=([^;]+)/)
  return m ? decodeURIComponent(m[1]) : ''
}

function getCsrfHeaders() {
  const token = getCsrfToken()
  return token ? { 'X-CSRFToken': token } : {}
}

// ──────────────────────────────────────────────────────────────────
// 同期系エンドポイント
// ──────────────────────────────────────────────────────────────────

export async function chatStart(uuid) {
  const res = await fetch(`${BASE}/chat/start`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getCsrfHeaders() },
    body: JSON.stringify({ uuid })
  })
  return parseJsonOrThrow(res)
}

export async function chatEnd(uuid) {
  const res = await fetch(`${BASE}/chat/end`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getCsrfHeaders() },
    body: JSON.stringify({ uuid })
  })
  return parseJsonOrThrow(res)
}

export async function getUsage(uuid) {
  const res = await fetch(`${BASE}/usage?uuid=${encodeURIComponent(uuid)}`, {})
  return parseJsonOrThrow(res)
}

export async function getConsent(uuid) {
  const res = await fetch(
    `${BASE}/consent?uuid=${encodeURIComponent(uuid)}`,
    {}
  )
  return parseJsonOrThrow(res)
}

export async function setConsent(uuid, consented) {
  const res = await fetch(`${BASE}/consent`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getCsrfHeaders() },
    body: JSON.stringify({ uuid, consented })
  })
  return parseJsonOrThrow(res)
}

/**
 * 計測イベントを DB(CareerAiChatLog role='event') にミラー保存する。
 * GA4(gtag) と二重送信し、会員属性と JOIN した分析を可能にする。
 * fire-and-forget（失敗は無視・UX を止めない）。
 */
export async function trackEvent(uuid, sessionId, type, params) {
  if (!uuid || !sessionId || !type) return
  try {
    await fetch(`${BASE}/event`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getCsrfHeaders() },
      body: JSON.stringify({
        uuid,
        session_id: sessionId,
        type,
        params: params || {}
      }),
      keepalive: true
    })
  } catch (_) {
    /* 計測失敗は無視 */
  }
}

export async function getJobMaster(uuid) {
  const res = await fetch(
    `${BASE}/jobs/master?uuid=${encodeURIComponent(uuid)}`,
    {}
  )
  return parseJsonOrThrow(res)
}

export async function getJobDetail(uuid, jobId) {
  const res = await fetch(
    `${BASE}/jobs/${jobId}?uuid=${encodeURIComponent(uuid)}`,
    {}
  )
  if (res.status === 404) return null
  return parseJsonOrThrow(res)
}

// ──────────────────────────────────────────────────────────────────
// SSE: streamChat
// ──────────────────────────────────────────────────────────────────

/**
 * SSE で /chat を叩き、イベントごとにハンドラを呼ぶ。
 *
 * @param {object} params
 * @param {string} params.uuid
 * @param {string} params.session_id
 * @param {string} params.message
 * @param {string} params.current_mode
 * @param {boolean} [params.mode_locked] ユーザーが機能を明示選択中か。
 *   true ならサーバー側でモデルの mode / suggest_mode を採用しない
 * @param {string} [params.kickoff_mode] 画面が先に出したボット発言のモード。
 *   本文はサーバー側が持ち、ユーザー発言の直前にログへ記録して履歴に含める
 * @param {object} handlers
 * @param {function} handlers.onMeta    ({remaining, session_id}) => void
 * @param {function} handlers.onThinking ({status}) => void
 * @param {function} handlers.onText    ({content}) => void   テキスト chunk 受信時
 * @param {function} handlers.onComplete ({mode, suggest_mode, search, job_ids, model_used}) => void
 * @param {function} handlers.onDone    ({remaining}) => void
 * @param {function} handlers.onError   ({code, message}) => void
 * @returns {Promise<void>} ストリーム完了時に解決
 */
export async function streamChat(params, handlers) {
  const safe = (fn, evt) => {
    if (typeof fn === 'function') {
      try {
        fn(evt)
      } catch (_) {
        /* ハンドラ例外は無視 */
      }
    }
  }

  const res = await fetch(`${BASE}/chat`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getCsrfHeaders() },
    body: JSON.stringify(params)
  })

  if (res.status === 429) {
    const json = await res.json().catch(() => ({}))
    safe(handlers.onError, {
      code: 'RATE_LIMIT',
      message: json.message || '上限に達しました',
      remaining: 0,
      // 加入導線の出し分けに使う。落とすとプライム会員にも導線が出る
      is_prime: json.is_prime,
      daily_limit: json.daily_limit
    })
    return
  }
  if (res.status === 403) {
    const json = await res.json().catch(() => ({}))
    safe(handlers.onError, {
      code: json.code || 'FORBIDDEN',
      message: json.message || '利用できません'
    })
    return
  }
  if (!res.ok || !res.body) {
    safe(handlers.onError, {
      code: 'HTTP_' + res.status,
      message: '通信エラーが発生しました'
    })
    return
  }

  const reader = res.body.getReader()
  const decoder = new TextDecoder('utf-8')
  let buffer = ''

  while (true) {
    let chunk
    try {
      chunk = await reader.read()
    } catch (e) {
      safe(handlers.onError, {
        code: 'STREAM_READ_ERROR',
        message: e.message || 'ストリーム読み取り失敗'
      })
      return
    }
    if (chunk.done) break

    const text = decoder.decode(chunk.value, { stream: true })
    const parsed = parseSSEChunks(text, buffer)
    buffer = parsed.buffer
    for (const ev of parsed.events) {
      switch (ev.event) {
        case 'meta':
          safe(handlers.onMeta, ev.data)
          break
        case 'thinking':
          safe(handlers.onThinking, ev.data)
          break
        case 'text':
          safe(handlers.onText, ev.data)
          break
        case 'assistant_break':
          safe(handlers.onAssistantBreak, ev.data)
          break
        case 'complete':
          safe(handlers.onComplete, ev.data)
          break
        case 'done':
          safe(handlers.onDone, ev.data)
          break
        case 'error':
          safe(handlers.onError, ev.data)
          break
        default:
          break
      }
    }
  }
}

// ──────────────────────────────────────────────────────────────────
// 内部
// ──────────────────────────────────────────────────────────────────

async function parseJsonOrThrow(res) {
  let json = null
  try {
    json = await res.json()
  } catch (_) {
    json = null
  }
  if (!res.ok) {
    const err = new Error((json && json.message) || `HTTP ${res.status}`)
    err.status = res.status
    err.code = (json && json.code) || ''
    err.payload = json
    throw err
  }
  return json
}

export default {
  chatStart,
  chatEnd,
  getUsage,
  getConsent,
  setConsent,
  trackEvent,
  getJobMaster,
  getJobDetail,
  streamChat
}
